import { IAdminDashboardCourseTrack } from 'components/admin/dashboard/types';
import { IDashboardFilters } from 'components/admin/types';
import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { ISwrConfiguration, useFetch } from 'lib/hooks';
import { IModule } from 'lib/types/course';
import { TrainingReminderCadence } from 'lib/types/notifications';
import { UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import {
  AssignmentState,
  IAdminAutomatedSchedulerConfiguration,
  IAdminCompanyTrainingDate,
  IAdminDashboardModule,
  IAdminDashboardTrainingGoal,
  ICompletionSummary,
  IDashboardAssignment,
  IDashboardNudgeRatingsSummary,
  IDashboardPhishingTrainingCampaign,
  IEnrollmentCount,
  INotificationSummary,
  TrainingGoalContentType,
} from '../types';

export * from './search';

export const useAssignment = (
  assignmentKey: string,
  options?: ISwrConfiguration
) => {
  const shouldFetch = !!assignmentKey && (options?.shouldFetch ?? true);
  return useFetch<IDashboardAssignment>(
    `/admin/dashboard/assignments/${assignmentKey}`,
    { ...options, shouldFetch }
  );
};

export const useAutomatedSchedulerConfig = (
  automatedSchedulerConfigId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!automatedSchedulerConfigId && (options?.shouldFetch ?? true);
  return useFetch<IAdminAutomatedSchedulerConfiguration>(
    `/admin/dashboard/automated-scheduler/${automatedSchedulerConfigId}`,
    { ...options, shouldFetch }
  );
};

export const useAutomatedSchedulerEnrollmentCounts = (
  automatedSchedulerConfigId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!automatedSchedulerConfigId && (options?.shouldFetch ?? true);
  return useFetch<IEnrollmentCount>(
    `/admin/dashboard/automated-scheduler/${automatedSchedulerConfigId}/enrollment-counts`,
    { ...options, shouldFetch }
  );
};

export const useAutomatedSchedulerNextTrainingDates = (
  automatedSchedulerConfigId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!automatedSchedulerConfigId && (options?.shouldFetch ?? true);
  return useFetch<string>(
    `/admin/dashboard/automated-scheduler/${automatedSchedulerConfigId}/next-training-dates`,
    { ...options, shouldFetch }
  );
};

export const useAutomatedSchedulerPreviousTrainingDates = (
  automatedSchedulerConfigId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!automatedSchedulerConfigId && (options?.shouldFetch ?? true);
  return useFetch<string>(
    `/admin/dashboard/automated-scheduler/${automatedSchedulerConfigId}/previous-training-dates`,
    { ...options, shouldFetch }
  );
};

export const useCompanyTrainingDate = (
  id: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch = !!id && (options?.shouldFetch ?? true);
  return useFetch<IAdminCompanyTrainingDate>(
    `/admin/dashboard/company-training-date/${id}`,
    { ...options, shouldFetch }
  );
};

export const useCompletionSummary = (
  queryParams: IDashboardFilters,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!queryParams.assignmentKeys?.length && (options?.shouldFetch ?? true);
  return useFetch<ICompletionSummary>(
    ['/admin/dashboard/reporting/assignments/completion-summary', queryParams],
    { ...options, shouldFetch }
  );
};

export const useCompletionSummaries = (
  assignmentStates: AssignmentState[],
  options?: ISwrConfiguration
) =>
  useFetch<ICompletionSummary[]>(
    [
      '/admin/dashboard/reporting/assignments/completion-summaries',
      { assignmentStates },
    ],
    options
  );

export const useCourseAssignments = (
  courseId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch = !!courseId && (options?.shouldFetch ?? true);
  return useFetch<IDashboardAssignment[]>(
    `/admin/dashboard/assignments/courses/${courseId}`,
    { ...options, shouldFetch }
  );
};

export const useDashboardAssignments = (
  assignmentStates: AssignmentState[],
  options?: ISwrConfiguration
) =>
  useFetch<IDashboardAssignment[]>(
    ['/admin/dashboard/assignments', { assignmentStates }],
    options
  );

interface IAdminGetModulesQueryParams {
  courseIds?: number[];
  moduleIds?: number[];
}

export const useAdminDashboardModules = (
  params: IAdminGetModulesQueryParams,
  options?: ISwrConfiguration
) =>
  useFetch<IAdminDashboardModule[]>(
    ['/admin/dashboard/modules', params],
    options
  );

interface IModuleParams {
  courseId: number;
  moduleIds?: number[];
  queryParams?: {
    includeDeleted?: boolean;
  };
}

/**
 * @deprecated Use `useAdminDashboardModules` in the `admin/dashboard` domain instead.
 */
export const useModules = (
  params: IModuleParams,
  options?: ISwrConfiguration
) => {
  const { courseId, moduleIds, queryParams } = params;
  const shouldFetch = !!courseId && (options?.shouldFetch ?? true);
  const res = useFetch<IModule[]>(
    [`/admin/courses/${courseId}/modules`, queryParams],
    {
      ...options,
      shouldFetch,
    }
  );
  return res.data
    ? {
        ...res,
        data: (moduleIds
          ? _.filter(res.data, (m) => _.includes(moduleIds, m.id))
          : res.data) as IModule[],
      }
    : res;
};

export const useTrainingGoalModules = (
  trainingGoal: IAdminDashboardTrainingGoal
) => {
  const isContinuousLearning =
    trainingGoal?.contentType === TrainingGoalContentType.CONTINUOUS_LEARNING;
  const isUserManaged =
    trainingGoal?.contentType === TrainingGoalContentType.USER_MANAGED;
  const isEthenaCampaign =
    trainingGoal?.contentType === TrainingGoalContentType.ETHENA_CAMPAIGN;
  const orderedModuleIds = isEthenaCampaign
    ? []
    : trainingGoal?.tracks[0].orderedModuleIds;
  return useAdminDashboardModules(
    {
      courseIds: trainingGoal?.courseIds,
      moduleIds: orderedModuleIds ?? [],
    },
    {
      shouldFetch: isContinuousLearning || isUserManaged,
    }
  );
};

export const useNotificationSummary = (
  courseId: number,
  queryParams: Omit<IDashboardFilters, 'completionStatuses'>,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!queryParams.assignmentKeys?.length && (options?.shouldFetch ?? true);
  return useFetch<INotificationSummary>(
    [`/admin/dashboard/courses/${courseId}/notification-summary`, queryParams],
    { ...options, shouldFetch }
  );
};

interface INudgeRatingsQueryParams {
  assignmentKeys: string | string[];
}

export const useNudgeRatings = (
  queryParams: INudgeRatingsQueryParams,
  options?: ISwrConfiguration
) => {
  const shouldFetch =
    !!queryParams.assignmentKeys?.length && (options?.shouldFetch ?? true);
  return useFetch<IDashboardNudgeRatingsSummary>(
    [`/admin/dashboard/reporting/assignments/ratings`, queryParams],
    { ...options, shouldFetch }
  );
};

export interface IPopulationCountQueryParams {
  assignmentKeys?: string[];
  departments?: string[];
  enrolledInAutomatedSchedulerCourseId?: number;
  locations?: string[];
  roles?: UserProfileRole[];
}

export const usePopulationCount = (
  queryParams?: IPopulationCountQueryParams,
  options?: ISwrConfiguration
) =>
  useFetch<number>(['/admin/dashboard/population-count', queryParams], options);

interface ITrainingReminderCadenceResponse {
  trainingReminderCadence: TrainingReminderCadence;
}

export const useTrainingReminderCadence = (options?: ISwrConfiguration) => {
  const res = useFetch<ITrainingReminderCadenceResponse>(
    '/admin/dashboard/training-reminder-cadence',
    options
  );
  return { ...res, data: res.data?.trainingReminderCadence };
};

export interface IAdminAssignmentCorrectnessSummary {
  moduleCorrectnessSummaries: IAdminModuleCorrectnessSummary[];
}

export interface IAdminModuleCorrectnessSummary {
  courseId: number;
  moduleId: number;
  moduleName: string;
  nudgeGroupCorrectnessSummaries: IAdminNudgeGroupCorrectnessSummary[];
}

export interface IAdminNudgeGroupCorrectnessSummary {
  nudgeGroupId: number;
  nudgeGroupName: string;
  questionCorrectnessSummaries: IAdminQuestionCorrectnessSummary[];
}

export interface IAdminQuestionCorrectnessSummary {
  questionId: number;
  questionText: string;
  reportingThresholdReached: boolean;
  totalCorrectFirstTry: number;
  totalIncorrectFirstTry: number;
}

export const useAssignmentCorrectnessSummary = (
  assignmentKey: string,
  options?: ISwrConfiguration
) =>
  useFetch<IAdminAssignmentCorrectnessSummary>(
    buildQueryParamLink('/admin/dashboard/reporting/correctness-summary', {
      assignmentKey,
    }),
    options
  );

export const usePhishingTrainingCampaign = (
  trainingCampaignId: number,
  options?: ISwrConfiguration
) => {
  const shouldFetch = !!trainingCampaignId && (options?.shouldFetch ?? true);
  return useFetch<IDashboardPhishingTrainingCampaign>(
    `/admin/dashboard/phishing-training-campaign/${trainingCampaignId}`,
    {
      ...options,
      shouldFetch,
    }
  );
};

export const useDashboardCourseTracks = (courseId: number) =>
  useFetch<IAdminDashboardCourseTrack[]>(
    `/admin/dashboard/courses/${courseId}/tracks`
  );
